<script setup>
import { ref, watch } from 'vue';
import UiDialog from './UiDialog.vue';
import UiFormFields from './UiFormFields.vue';

// A dialog holding one form: validates, then emits `submit`; the page does
// the request (see useAsyncAction) and closes it. Simple forms pass a
// `fields` schema and v-model:values; anything custom goes in the default slot.
//
//   <UiFormDialog v-model="open" title="Add strand" v-model:values="form"
//     :fields="[{ key: 'name', label: 'Name', required: true }]"
//     :saving="saving" submit-text="Add strand" @submit="save" />
const props = defineProps({
  title: { type: String, required: true },
  description: { type: String, default: '' },
  size: { type: String, default: 'sm' },
  fields: { type: Array, default: () => [] },
  // 'stack' = one column; 'grid' = two columns (fields may set span: 2)
  layout: { type: String, default: 'stack' },
  saving: { type: Boolean, default: false },
  submitText: { type: String, default: 'Save' },
  cancelText: { type: String, default: 'Cancel' },
  // View-only: fields disabled, no submit button, Close instead of Cancel.
  readonly: { type: Boolean, default: false },
});
const open = defineModel({ type: Boolean, default: false });
const values = defineModel('values', { type: Object, default: null });
const emit = defineEmits(['submit']);

const formRef = ref(null);

watch(open, (isOpen) => {
  if (isOpen) formRef.value?.resetValidation();
});

async function submit() {
  if (props.readonly) return;
  const { valid } = await formRef.value.validate();
  if (valid) emit('submit');
}
</script>

<template>
  <UiDialog
    v-model="open"
    :title="title"
    :description="description"
    :size="size"
    :persistent="!readonly"
  >
    <v-form
      ref="formRef"
      :class="layout === 'grid' ? 'form-grid' : 'form-stack'"
      :disabled="readonly"
      @submit.prevent="submit"
    >
      <UiFormFields v-if="fields.length && values" v-model:values="values" :fields="fields" />
      <slot />
    </v-form>

    <template #footer="{ close }">
      <slot name="footer-start" />
      <v-btn variant="outlined" @click="close">{{ readonly ? 'Close' : cancelText }}</v-btn>
      <v-btn v-if="!readonly" color="primary" :loading="saving" @click="submit">
        {{ submitText }}
      </v-btn>
    </template>
  </UiDialog>
</template>
